import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { getGallery, scoreTone } from "@/lib/api";

export default function GalleryTeaser() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    getGallery().then((d) => setItems((d.items || []).slice(0, 4))).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="bg-gradient-to-b from-blue-50 to-white py-20 md:py-28 overflow-hidden border-y border-blue-100/70" data-testid="gallery-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-blue-700" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">AI Rebuild Gallery</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 max-w-2xl leading-[1.08]">
              See real websites reimagined by AI
            </h2>
            <p className="text-slate-600 text-base mt-4 max-w-xl">
              Fresh heros, palettes and conversion strategies — generated from live analyses. Yours takes seconds.
            </p>
          </div>
          <Link to="/gallery" data-testid="gallery-teaser-link" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-xl px-6 py-3.5 hover:-translate-y-0.5 transition-transform shrink-0">
            View the full gallery <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((item, idx) => {
            const tone = scoreTone(item.overall_score ?? 0);
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.45, delay: idx * 0.08 }}
                className="bg-white border border-slate-200 card-shadow rounded-2xl overflow-hidden group"
              >
                <div className="h-24 flex">
                  {(item.palette || []).length ? item.palette.map((p, i) => (
                    <div key={i} className="flex-1 transition-all group-hover:flex-[1.25]" style={{ background: p.hex }} />
                  )) : <div className="flex-1 bg-slate-200" />}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-mono text-slate-400 truncate">{item.domain}</p>
                    {item.overall_score != null && (
                      <span className="font-heading text-sm font-extrabold" style={{ color: tone.color }}>{item.overall_score}</span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mt-1.5 line-clamp-2 leading-snug">{item.headline || "Modern redesign"}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
